import { DateTime } from 'luxon'
import { Skill, SkillJson } from '../../skills/models/skill'
import { Building, BuildingDto, buildingFromDto } from '../../buildings/models/building'
import { Address, AddressDto, addressFromDto } from '../../addresses/models/address'

export interface AreaDisplaySingleFieldConfig<T> {
    type: 'field'
    value: keyof T
}

export interface AreaDisplayExpressionFieldConfig {
    type: 'expression'
    value: string
}

export type AreaDisplayFieldConfig<T> = AreaDisplaySingleFieldConfig<T> | AreaDisplayExpressionFieldConfig

export const defaultDisplayNameConfig: AreaDisplayExpressionFieldConfig = {
    type: 'expression',
    value: '${building.customFields.business_unit} - ${name} - ${building.name}'
}

export const areaNestedValue = (obj: unknown, path: string): string | undefined => {
    if (typeof obj !== 'object' || obj === null) {
        return undefined
    }

    const keys = path.split('.')
    let currentValue: unknown = obj
    for (const key of keys) {
        if (currentValue && typeof currentValue === 'object' && key in currentValue) {
            currentValue = (currentValue as Record<string, unknown>)[key]
        }
    }

    return typeof currentValue === 'string' ? currentValue : undefined
}

export const areaDisplayName = (area: Area, config: AreaDisplayFieldConfig<Area> = defaultDisplayNameConfig): string => {
    if (config.type === 'field') {
        return area[config.value] as string
    }

    if (config.type === 'expression') {
        return config.value.replace(/\${([^}]+)}/g, (_, key) => {
            const value = areaNestedValue(area, key)
            return value !== undefined ? value : ''
        })
    }

    return area.name
}

declare const AreaIDSymbol: unique symbol
export type AreaID = string & { [AreaIDSymbol]: never }

export interface AreaDto {
    id: string
    name: string
    color_hex: string
    archived_at: string | null

    permissions: { can_edit: boolean }

    skills?: { data: SkillJson[] }
    building?: { data: BuildingDto }
    address?: { data: AddressDto }
}

export interface Area {
    id: AreaID
    name: string
    colorHex: string
    archivedAt: DateTime | null

    permissions: { canEdit: boolean }

    skills: Skill[]

    building: Building | null

    address: Address | null
}

export const createArea = (
    params: {
        id: AreaID,
        name: string,
        colorHex: string,
        archivedAt: DateTime | null,

        permissions: { canEdit: boolean },

        skills: Skill[],

        building: Building | null,

        address: Address | null
    }
): Area => ({
    id: params.id,
    name: params.name,
    colorHex: params.colorHex,
    archivedAt: params.archivedAt,

    permissions: params.permissions,

    skills: params.skills,

    building: params.building,

    address: params.address,
})

export const areaFromDto = (dto: AreaDto): Area => (createArea({
    id: dto.id as AreaID,
    name: dto.name,
    colorHex: dto.color_hex,
    archivedAt: dto.archived_at ? DateTime.fromISO(dto.archived_at) : null,

    permissions: { canEdit: dto.permissions.can_edit },

    skills: dto.skills?.data?.map((skillJson) => (Skill.fromJson(skillJson))) ?? [],

    building: dto.building?.data ? buildingFromDto(dto.building.data) : null,

    address: dto.address?.data ? addressFromDto(dto.address.data) : null,
}))
